import type { SupabaseClient } from '@supabase/supabase-js';
import { InfrastructureError } from '@dma/shared';
import { Article, type ArticleCategory, type ArticleStatus } from '../domain/entities/Article.entity';
import type { ArticleRepository } from '../domain/repositories/ArticleRepository.interface';

const TABLE_NAME = 'articles';

interface ArticleRow {
  id: string;
  slug: string;
  title: string;
  category: ArticleCategory;
  summary: string;
  body: string;
  cover_image: string | null;
  published_at: string | null;
  status: ArticleStatus;
  created_at: string;
  updated_at: string;
}

function rowToEntity(row: ArticleRow): Article {
  return Article.create({
    id: row.id,
    slug: row.slug,
    title: row.title,
    category: row.category,
    summary: row.summary,
    body: row.body,
    coverImage: row.cover_image,
    publishedAt: row.published_at,
    status: row.status,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  });
}

function entityToRow(entity: Article): ArticleRow {
  const { props } = entity;
  return {
    id: props.id,
    slug: props.slug,
    title: props.title,
    category: props.category,
    summary: props.summary,
    body: props.body,
    cover_image: props.coverImage,
    published_at: props.publishedAt,
    status: props.status,
    created_at: props.createdAt,
    updated_at: props.updatedAt
  };
}

/**
 * Infrastructure implementation backed by the `articles` Supabase table (see
 * infra/supabase/migrations/004_create_articles.sql and
 * infra/supabase/policies/005_articles_policies.sql). Mirrors
 * SupabaseScientificEntryRepository's shape exactly — same public/admin
 * read split, same row<->entity mapping pattern.
 */
export class SupabaseArticleRepository implements ArticleRepository {
  constructor(private readonly client: SupabaseClient) {}

  public async getPublished(category: ArticleCategory): Promise<Article[]> {
    const { data, error } = await this.client
      .from(TABLE_NAME)
      .select('*')
      .eq('category', category)
      .eq('status', 'published')
      .order('published_at', { ascending: false })
      .returns<ArticleRow[]>();

    if (error) {
      throw new InfrastructureError('ARTICLES_LIST_FAILED', error.message, { cause: error });
    }
    return (data ?? []).map(rowToEntity);
  }

  public async getPublishedBySlug(category: ArticleCategory, slug: string): Promise<Article | null> {
    const { data, error } = await this.client
      .from(TABLE_NAME)
      .select('*')
      .eq('category', category)
      .eq('slug', slug)
      .eq('status', 'published')
      .maybeSingle<ArticleRow>();

    if (error) {
      throw new InfrastructureError('ARTICLE_FETCH_FAILED', error.message, { cause: error });
    }
    return data ? rowToEntity(data) : null;
  }

  public async getAllForAdmin(category: ArticleCategory): Promise<Article[]> {
    const { data, error } = await this.client
      .from(TABLE_NAME)
      .select('*')
      .eq('category', category)
      .order('updated_at', { ascending: false })
      .returns<ArticleRow[]>();

    if (error) {
      throw new InfrastructureError('ARTICLES_ADMIN_LIST_FAILED', error.message, { cause: error });
    }
    return (data ?? []).map(rowToEntity);
  }

  public async getById(id: string): Promise<Article | null> {
    const { data, error } = await this.client
      .from(TABLE_NAME)
      .select('*')
      .eq('id', id)
      .maybeSingle<ArticleRow>();

    if (error) {
      throw new InfrastructureError('ARTICLE_FETCH_FAILED', error.message, { cause: error });
    }
    return data ? rowToEntity(data) : null;
  }

  public async save(entity: Article): Promise<void> {
    const { error } = await this.client.from(TABLE_NAME).upsert(entityToRow(entity));
    if (error) {
      throw new InfrastructureError('ARTICLE_SAVE_FAILED', error.message, { cause: error });
    }
  }

  public async delete(id: string): Promise<void> {
    const { error } = await this.client.from(TABLE_NAME).delete().eq('id', id);
    if (error) {
      throw new InfrastructureError('ARTICLE_DELETE_FAILED', error.message, { cause: error });
    }
  }
}
